export enum UserRole {
  Collaborator = 'Collaborator',
  Admin = 'Admin',
  FacebookPageCollaborator = 'Facebook page collaborator',
  Owner = 'Owner',
  LiveChatAgent = 'LiveChat agent',
  MonitorLeads = 'Monitor Leads',
}

export enum NewUserRole {
  FacebookPageCollaborator = 'Editor',
  LiveChatAgent = 'Live Chat Agent',
  Collaborator = 'Company Editor',
  MonitorLeads = 'Monitor Leads',
  Admin = 'Company Admin',
  Owner = 'Company Owner',
}

export const OldToNewRolesMap = {
  [NewUserRole.Admin]: UserRole.Admin,
  [NewUserRole.Collaborator]: UserRole.Collaborator,
  [NewUserRole.FacebookPageCollaborator]: UserRole.FacebookPageCollaborator,
  [NewUserRole.LiveChatAgent]: UserRole.LiveChatAgent,
  [NewUserRole.MonitorLeads]: UserRole.MonitorLeads,
  [NewUserRole.Owner]: UserRole.Owner,
};

export function isLiveChatAgentOrFacebookPageCollaborator(role: string) {
  return [
    UserRole.FacebookPageCollaborator,
    UserRole.LiveChatAgent,
    UserRole.MonitorLeads,
  ].includes(role as UserRole);
}

export const FormattedRoles = {
  [UserRole.FacebookPageCollaborator]: 'Editor',
  [UserRole.Admin]: 'Company Admin',
  [UserRole.Collaborator]: 'Company Editor',
  [UserRole.LiveChatAgent]: 'Live Chat Agent',
  [UserRole.MonitorLeads]: 'Monitor Leads',
};

export function formatUserRole(role: string) {
  return FormattedRoles[role] || role;
}

export enum OnboardingSignupStep {
  NONE = 'none',
  CONNECT_PAGE = 'connect_page',
  USER_INFO = 'user_info',
  INSTALLATION = 'xray_installation',
  BOOK_DEMO = 'demo',
}

export type UnselectedFbPages = { [key: string]: number[] };

export type OneTimeConfirmationModalType =
  | 'inbox_linked_convo_assignment'
  | 'inbox_linked_convo_status_update';

export type OneTimeAlertType =
  | 'november_2020_euro_ads_changes_alert'
  | 'dec_2020_fb_api_europe_changes';

export type UTM = {
  utmSource?: string;
  utmCampaign?: string;
  utmMedium?: string;
  utmContent?: string;
};

export type UserAttributes = {
  buyBeforeTrying?: boolean;
  mmBuyBeforeTrying: boolean;
  hideWelcomerDescription: boolean;
  seenLastPromo: boolean;
  appsumoIcPromo?: boolean;
  instagramHandle?: string;
  instagramFollowers?: number;
  instachampOfferEndsAt?: string;
  instachampOffer?: {
    name?: string;
    email?: string;
    otherUsers?: string;
  } | null;
  seenUpgradeTeamPromo: boolean;
  seenNewDashboardModal: boolean;
  activeCompanyId?: number;
  onboardingSignupBot?: number;
  onboardingSignupStep: OnboardingSignupStep;
  onboardingReportVersion: 'A' | 'B';
  onboardingCompanyInfoStep?: number;
  activeChatbotId?: number;
  seenFreeConsultingPromo?: boolean;
  seenFbAdsCouponModal?: boolean;
  seenWelcomeNewUiModal?: boolean;
  seenWelcomeSeriesModal?: boolean;
  lcFilterSetting?: string;
  hasMobile: boolean;
  hideMobileDownload?: boolean;
  chatbotTourStep?: number | null;
  seenIcAppsumoPromo?: boolean;
  skipXrayInstallation?: boolean;
  seenHowManyPeopleAccessYourInboxQuestion?: boolean;
  seenEnterPhoneNumberModal?: boolean;
  seenIcSmsActivationBanner?: boolean;
  seenIcAutoresponderOnboardingFlow?: boolean | null;
  seenNewBilling2023Modal?: boolean | null;
  seenFreeCreditsModal?: boolean | null;
  hideKeywordsMatchTooltip?: boolean;
  seenQqSplitAlert?: boolean;
  seenNewLeftMenuTour?: boolean;
  seenTour: {
    xray?: boolean;
    leads?: boolean;
    restore?: boolean;
  };
  pushNotifications: {
    allMessages?: boolean;
    allMessagesUnselectedPages?: UnselectedFbPages;
    needsFollowUp?: boolean;
    needsFollowUpUnselectedPages?: UnselectedFbPages;
    widgetTriggered?: boolean;
    widgetTriggeredUnselectedPages?: UnselectedFbPages;
    assignedToMe?: boolean;
    assignedToMeUnselectedPages?: UnselectedFbPages;
    unicornLead?: boolean;
    unicornLeadUnselectedPages?: UnselectedFbPages;
  };
  desktopNotifications: {
    seenDesktopNotificationsPromoPopup: boolean;
    desktopDevices: string[];
    allMessages?: boolean;
    allMessagesUnselectedPages?: UnselectedFbPages;
    needsFollowUp: boolean;
    needsFollowUpUnselectedPages?: UnselectedFbPages;
    widgetTriggered?: boolean;
    widgetTriggeredUnselectedPages?: UnselectedFbPages;
    assignedToMe?: boolean;
    assignedToMeUnselectedPages?: UnselectedFbPages;
    unicornLead?: boolean;
    unicornLeadUnselectedPages?: UnselectedFbPages;
  };
  lastSeenWhatsNewId?: number | null;
  betaUiEnabled?: boolean;
  signInDeeplink?: string | null;
  oneTimeConfirmationModalsSeen?: Partial<
    Record<OneTimeConfirmationModalType, boolean>
  >;
  oneTimeAlertsSeen?: Partial<Record<OneTimeAlertType, boolean>>;
  instagramSignup?: {
    businessName: string;
    phoneNumber: string;
  };
  instachampEnrollments: {
    [account: string]: number;
  };
  freeTrial?: boolean;
  trialPaywallSeen?: boolean;
  showPaywall?: boolean;
  semrushTermsOfServiceAgreed?: boolean;
  fbAutoresponderWaitlist: null | {
    useAutorespondersOnFbFor: string;
    goalOnFb: string;
  };
  language?: string;
  requestedDemo?: boolean;
  agency?: boolean;
  gohighlevel?: boolean;
  stopShowingWalkthroughVideo?: boolean;
  smsConsent?: boolean;
} & UTM;

export type UserRoleCode =
  | 'collaborator'
  | 'admin'
  | 'owner'
  | 'facebook_page_collaborator'
  | 'live_chat_agent'
  | 'monitor_leads';

export enum UserRoleMappedToBECode {
  'collaborator' = 0,
  'admin' = 1,
  'owner' = 2,
  'facebook_page_collaborator' = 3,
  'live_chat_agent' = 4,
  'monitor_leads' = 5,
}

export type RoleFlags = {
  isCollaborator: boolean;
  isAdmin: boolean;
  isFacebookPageCollaborator: boolean;
  isLiveChatAgent: boolean;
  isMonitorLeadsRole: boolean;
  isLimitedRole: boolean;
};

export function getRoleFlagsByCode(role?: string): RoleFlags {
  return {
    isCollaborator: role === 'collaborator',
    isAdmin: role === 'admin',
    isFacebookPageCollaborator: role === 'facebook_page_collaborator',
    isLiveChatAgent: role === 'live_chat_agent',
    isMonitorLeadsRole: role === 'monitor_leads',
    isLimitedRole: role === 'live_chat_agent' || role === 'monitor_leads',
  };
}

export default interface CompanyUsersModel {
  id: number;
  firstName: string;
  lastName: string;
  admin: boolean;
  csAdmin: boolean;
  email: string;
  createdAt: string;
  role: string;
  allowedFacebookPageIds: number[] | null;
  userAttributes: UserAttributes;
  permittedForFacebookPage: boolean;
  // Note: Later can make it required, but users will need to re-login first.
  searchToken?: string;
  image?: string;
  roleCode?: UserRoleCode;
  personaName: string;
  personaAssetUrl: string;
  away: boolean;
  awayUntil: null | string;
  facebookTokenExists: boolean;
  googleSheetsActivated: boolean;
  mailchimpActivated: boolean;
  // Note: BE currently is only returning it till stopShowingWalkthroughVideo user attribute is set and no more than 10 counts anyway.
  sessionsCount?: number;
}

// TODO try to get rid of this
export const USER_ROLES: Record<string, UserRoleCode> = {
  Collaborator: 'collaborator',
  Admin: 'admin',
  'Facebook page collaborator': 'facebook_page_collaborator',
  [UserRole.LiveChatAgent]: 'live_chat_agent',
  [UserRole.MonitorLeads]: 'monitor_leads',
};

export const INVERTED_USER_ROLES: { [key: string]: any } = Object.keys(
  USER_ROLES,
).reduce((obj, key) => ({ ...obj, [USER_ROLES[key]]: key }), {});
